import React, { useState } from 'react';
import { ActionsExportForm, ExportAccessToggle, Modal } from './comps';
import styled from 'styled-components';
import { Poppins, Spacer } from 'src/common';
import Button from '../form/Button';
import CheckBox from '../form/CheckBox';
import { gaEventEXPORT } from 'src/utils/gaEvent';
import { ControlsQuery } from 'src/graphql/_generated';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { ActionsConf } from 'src/api/types/types';
import { useExportJob } from 'src/hooks/useExportJob';

const StyledModal = styled(Modal)`
  width: 566px;
  min-height: 200px;
  overflow-y: auto;
  max-height: 100%;
  padding: 0 !important;

  .container {
    display: grid;
  }
  .subtitle {
    color: ${({ theme }) => theme.colors.davysGrey};
    font-size: 14px;
  }
  .scenarios-switch {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__title {
      width: 200px;
    }

    &__switch {
      display: grid;
      grid-template-columns: auto auto auto;
      grid-gap: 8px;
      align-items: center;
      justify-content: space-between;

      span {
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.cflowerBlue};
      }
    }
  }

  .checklist {
    &__row {
      margin-bottom: 11px;
      display: flex;
      grid-gap: 11px;
      align-items: center;
      user-select: none;

      &__label {
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.davysGrey};
      }
    }
  }

  .divider {
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.stroke};
  }
`;

type ControlGql = ControlsQuery['controls'][number];

interface PdfExportModalRMProps {
  onClose: () => void;
  controls: ControlGql[];
}

export const PdfExportModalRM: React.FC<PdfExportModalRMProps> = ({ onClose, controls }) => {
  const [selectedControls, setSelectedControls] = useState<string[]>(controls.map((el) => el.id));
  const [includesActions, setIncludesActions] = useState<ActionsConf>({ enabled: true, completionStatus: 'ALL' });
  const [isPublic, setIsPublic] = useState(true);
  const { triggerExport, loading } = useExportJob({ onClose });

  const handleExport = () => {
    gaEventEXPORT('EXPORT_PDF');
    mpEvent(MPEvents.ButtonClick, {
      button: 'Export',
      modal: 'Pdf export modal',
    });
    triggerExport(
      {
        type: 'rmp',
        format: 'pdf',
        controlIds: selectedControls,
        includesActions,
      },
      { isPublic },
    );
  };

  const selectAll = () => {
    if (selectedControls.length === controls.length) {
      setSelectedControls([]);
    } else {
      setSelectedControls(controls.map((el) => el.id) || []);
    }
  };

  return (
    <>
      <StyledModal onRequestClose={() => onClose()} isOpen isLoading={loading} persistent={loading}>
        <Spacer $px={54} />
        <div className="h-padding">
          <Poppins className="m-title" px={28}>
            Export PDF
          </Poppins>
        </div>
        <Spacer $px={15} />
        <div className="divider" />
        <Spacer $px={24} />
        <div className="h-padding">
          <Poppins className="subtitle">You can choose which controls to include in the export</Poppins>
          <Spacer $px={19} />
          <div className="checklist">
            <div className="checklist__row">
              <CheckBox isChecked={selectedControls.length === controls.length} onClick={selectAll} />
              <Poppins className="checklist__row__label" nowrap>
                All controls
              </Poppins>
            </div>
            {controls.map((el) => (
              <div className="checklist__row" key={el.id}>
                <CheckBox
                  isChecked={selectedControls.includes(el.id)}
                  onClick={() => {
                    if (selectedControls.includes(el.id)) {
                      setSelectedControls(selectedControls.filter((item) => item !== el.id));
                    } else {
                      setSelectedControls([...selectedControls, el.id]);
                    }
                  }}
                />
                <Poppins className="checklist__row__label" nowrap>
                  {el.name}
                </Poppins>
              </div>
            ))}
          </div>
          <Spacer $px={24} />
          <ActionsExportForm includesActions={includesActions} setIncludesActions={setIncludesActions} />
          <Spacer $px={16} />
          <div className="divider" />
          <Spacer $px={16} />
          <ExportAccessToggle value={isPublic} onChange={setIsPublic} />
          <Spacer $px={32} />
          <div className="container">
            <Button
              disabled={loading || selectedControls.length === 0}
              loading={loading}
              primary
              onClick={handleExport}
              data-cy="export"
            >
              EXPORT
            </Button>
          </div>
        </div>
        <Spacer $px={47} />
      </StyledModal>
    </>
  );
};
